import { OFFICERS, OfficerData } from './officers'
import { readPointMetricConfig } from './pointSystemConfig'

export interface MonthOption {
  key: string
  label: string
  shortLabel: string
  isCurrent: boolean
}

export interface RosterEntry {
  name: string
  rank: string
  points: number
  goal: number
  goalPercent: number
  qualified: boolean
  pointsDelta: number | null
  unitsClosed: number
  creditPulls: number
  docOuts: number
  submissions: number
  closings: number
  speedBonusFastCloses: number
  speedBonusPoints: number
  callAnswerRate: number
  dials: number
}

export interface MonthSummary {
  totalPoints: number
  averagePoints: number
  qualifiedCount: number
  officerCount: number
  totalUnits: number
  averageAnswerRate: number
  topPerformer: string
  totalPointsDelta: number | null
}

const CURRENT_MONTH_KEY = '2025-05'
const MONTH_COUNT = 12

function buildMonthKeys(): string[] {
  const [year, month] = CURRENT_MONTH_KEY.split('-').map(Number)
  return Array.from({ length: MONTH_COUNT }, (_, index) => {
    const date = new Date(year, month - 1 - index, 1)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
  })
}

export const MONTH_KEYS = buildMonthKeys()
export const CURRENT_MONTH = MONTH_KEYS[0]

function monthDate(key: string): Date {
  const [year, month] = key.split('-').map(Number)
  return new Date(year, month - 1, 1)
}

export function formatMonthLabel(key: string): string {
  return monthDate(key).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

export const MONTH_OPTIONS: MonthOption[] = MONTH_KEYS.map((key, index) => ({
  key,
  label: formatMonthLabel(key),
  shortLabel: monthDate(key).toLocaleDateString('en-US', {
    month: 'short',
    year: '2-digit',
  }),
  isCurrent: index === 0,
}))

// Deterministic per-officer, per-month variance so lookback months stay stable
// across renders without needing stored history for every past month.
function seededFactor(seed: string, min: number, max: number): number {
  let hash = 2166136261
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  const normalized = ((hash >>> 0) % 10000) / 10000
  return min + normalized * (max - min)
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function roundTo(value: number, step: number): number {
  return Math.round(value / step) * step
}

function pointsForMonth(officer: OfficerData, monthKey: string): number {
  if (monthKey === CURRENT_MONTH) return officer.monthlyPoints
  const factor = seededFactor(`${officer.name}:${monthKey}`, 0.58, 1.18)
  return roundTo(officer.monthlyPoints * factor, 50)
}

function entryForMonth(officer: OfficerData, monthKey: string): RosterEntry {
  const isCurrent = monthKey === CURRENT_MONTH
  const factor = isCurrent
    ? 1
    : seededFactor(`${officer.name}:${monthKey}`, 0.58, 1.18)
  const points = pointsForMonth(officer, monthKey)

  return {
    name: officer.name,
    rank: officer.currentRank,
    points,
    goal: officer.monthlyGoal,
    goalPercent: Math.round((points / officer.monthlyGoal) * 100),
    qualified: points >= officer.monthlyGoal,
    pointsDelta: null,
    unitsClosed: Math.max(0, Math.round(officer.unitsClosed * factor)),
    creditPulls: Math.max(0, Math.round(officer.creditPulls * factor)),
    docOuts: Math.max(0, Math.round(officer.docOuts * factor)),
    submissions: Math.max(0, Math.round(officer.submissions * factor)),
    closings: Math.max(0, Math.round(officer.closings * factor)),
    speedBonusFastCloses: Math.max(
      0,
      Math.round(officer.speedBonusFastCloses * factor),
    ),
    speedBonusPoints: Math.max(
      0,
      roundTo(officer.speedBonusPoints * factor, 250),
    ),
    callAnswerRate: clamp(
      Math.round(officer.callAnswerRate * (0.9 + factor * 0.1)),
      54,
      99,
    ),
    dials: Math.max(0, roundTo(officer.dials * factor, 10)),
  }
}

function priorMonthKey(monthKey: string): string | null {
  const index = MONTH_KEYS.indexOf(monthKey)
  if (index === -1) return null
  return MONTH_KEYS[index + 1] ?? null
}

export function getRosterForMonth(monthKey: string): RosterEntry[] {
  const previousKey = priorMonthKey(monthKey)
  return Object.values(OFFICERS)
    .map((officer) => {
      const entry = entryForMonth(officer, monthKey)
      return {
        ...entry,
        pointsDelta: previousKey
          ? entry.points - pointsForMonth(officer, previousKey)
          : null,
      }
    })
    .sort((a, b) => b.points - a.points)
}

export function getMonthSummary(monthKey: string): MonthSummary {
  const roster = getRosterForMonth(monthKey)
  const totalPoints = roster.reduce((sum, entry) => sum + entry.points, 0)
  const previousKey = priorMonthKey(monthKey)
  const previousTotal = previousKey
    ? getRosterForMonth(previousKey).reduce(
        (sum, entry) => sum + entry.points,
        0,
      )
    : null

  return {
    totalPoints,
    averagePoints: roster.length ? Math.round(totalPoints / roster.length) : 0,
    qualifiedCount: roster.filter((entry) => entry.qualified).length,
    officerCount: roster.length,
    totalUnits: roster.reduce((sum, entry) => sum + entry.unitsClosed, 0),
    averageAnswerRate: roster.length
      ? Math.round(
          roster.reduce((sum, entry) => sum + entry.callAnswerRate, 0) /
            roster.length,
        )
      : 0,
    topPerformer: roster[0]?.name ?? '—',
    totalPointsDelta:
      previousTotal === null ? null : totalPoints - previousTotal,
  }
}

export interface MetricBreakdownRow {
  id: string
  label: string
  category: string
  volume: number
  volumeLabel: string
  pointsEach: number | null
  points: number
  detail: string
}

export interface OfficerScorecard {
  name: string
  rank: string
  monthLabel: string
  entry: RosterEntry
  rows: MetricBreakdownRow[]
  earnedPoints: number
  deductedPoints: number
}

function configuredPoints(metricId: string, fallback: number): number {
  const metric = readPointMetricConfig().find((item) => item.id === metricId)
  if (!metric || !metric.enabled) return fallback
  return metric.points
}

function answerRateTier(
  rate: number,
): { metricId: string; label: string; fallback: number } | null {
  if (rate >= 95)
    return {
      metricId: 'answer-rate-95',
      label: '95%+ answer rate',
      fallback: 1000,
    }
  if (rate >= 90)
    return {
      metricId: 'answer-rate-90',
      label: '90%+ answer rate',
      fallback: 500,
    }
  if (rate >= 80)
    return {
      metricId: 'answer-rate-80',
      label: '80%+ answer rate',
      fallback: 250,
    }
  if (rate < 70)
    return {
      metricId: 'answer-rate-below-70',
      label: 'Below 70% answer rate',
      fallback: -1000,
    }
  return {
    metricId: 'answer-rate-below-80',
    label: 'Below 80% answer rate',
    fallback: 0,
  }
}

export function getOfficerScorecard(
  name: string,
  monthKey: string,
): OfficerScorecard | null {
  const officer = OFFICERS[name]
  if (!officer) return null

  const entry =
    getRosterForMonth(monthKey).find((item) => item.name === name) ??
    entryForMonth(officer, monthKey)

  const creditPullPoints = configuredPoints('credit-pull', 750)
  const docOutPoints = configuredPoints('doc-out', 500)
  const submissionPoints = configuredPoints('submission', 500)
  const closingPoints = configuredPoints('closing', 250)
  const dialPoints = configuredPoints('per-dial', 1)
  const tier = answerRateTier(entry.callAnswerRate)
  const tierPoints = tier ? configuredPoints(tier.metricId, tier.fallback) : 0

  const rows: MetricBreakdownRow[] = [
    {
      id: 'credit-pull',
      label: 'Credit Pulls',
      category: 'Core Production',
      volume: entry.creditPulls,
      volumeLabel: `${entry.creditPulls} pulls`,
      pointsEach: creditPullPoints,
      points: entry.creditPulls * creditPullPoints,
      detail: 'Credit report pulled on an active file',
    },
    {
      id: 'doc-out',
      label: 'Doc Outs Sent',
      category: 'Core Production',
      volume: entry.docOuts,
      volumeLabel: `${entry.docOuts} sent`,
      pointsEach: docOutPoints,
      points: entry.docOuts * docOutPoints,
      detail: 'Initial disclosures sent to the borrower',
    },
    {
      id: 'submission',
      label: 'Submissions',
      category: 'Core Production',
      volume: entry.submissions,
      volumeLabel: `${entry.submissions} submitted`,
      pointsEach: submissionPoints,
      points: entry.submissions * submissionPoints,
      detail: 'Complete package submitted to a lender',
    },
    {
      id: 'closing',
      label: 'Closings',
      category: 'Core Production',
      volume: entry.closings,
      volumeLabel: `${entry.closings} closed`,
      pointsEach: closingPoints,
      points: entry.closings * closingPoints,
      detail: 'Loan closed and funded',
    },
    {
      id: 'speed-bonus',
      label: 'Speed Bonuses',
      category: 'Speed & Quality Bonuses',
      volume: entry.speedBonusFastCloses,
      volumeLabel: `${entry.speedBonusFastCloses} fast closes`,
      pointsEach: null,
      points: entry.speedBonusPoints,
      detail: 'Closed within 14 days of credit pull',
    },
    {
      id: tier?.metricId ?? 'answer-rate-80',
      label: tier?.label ?? 'Answer rate',
      category: 'Response Time',
      volume: entry.callAnswerRate,
      volumeLabel: `${entry.callAnswerRate}% answered`,
      pointsEach: null,
      points: tierPoints,
      detail: 'Monthly answer-rate tier bonus',
    },
    {
      id: 'per-dial',
      label: 'Dials',
      category: 'Dial Activity',
      volume: entry.dials,
      volumeLabel: `${entry.dials.toLocaleString()} dials`,
      pointsEach: dialPoints,
      points: entry.dials * dialPoints,
      detail: 'Completed outbound dials logged',
    },
  ]

  return {
    name: officer.name,
    rank: entry.rank,
    monthLabel: formatMonthLabel(monthKey),
    entry,
    rows,
    earnedPoints: rows
      .filter((row) => row.points > 0)
      .reduce((sum, row) => sum + row.points, 0),
    deductedPoints: rows
      .filter((row) => row.points < 0)
      .reduce((sum, row) => sum + row.points, 0),
  }
}

export function getOfficerTrend(
  name: string,
): { monthKey: string; label: string; points: number }[] {
  const officer = OFFICERS[name]
  if (!officer) return []
  return [...MONTH_KEYS]
    .reverse()
    .map((monthKey) => ({
      monthKey,
      label: monthDate(monthKey).toLocaleDateString('en-US', {
        month: 'short',
      }),
      points: pointsForMonth(officer, monthKey),
    }))
}
